import { type ReactElement, useEffect, useRef } from 'react';
import { STORE_FILTER_DRAWER_ID } from '@/shared/config';
import { StoreFilterPanel, type StoreFilterPanelProps } from './store-filter-panel';

/** 포커스 트랩이 순회 대상으로 삼는 요소. */
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface StoreFilterDrawerProps extends StoreFilterPanelProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
}

/**
 * 필터 드로어 — 모든 폭에서 오른쪽에 화면 높이의 흰 판(최대 28rem, 좁은 화면은 전폭)이 열린다.
 * 목록은 카테고리 탭·툴바만 두고 전폭을 쓰며, 색상·수량·단가 조건은 이 판에서만 고른다.
 * 그림자 대신 뒤를 덮는 면과 1px 테두리로 분리한다. Escape·바깥 누름·CLOSE 로 닫힌다.
 */
export const StoreFilterDrawer = ({
  isOpen,
  onClose,
  ...panelProps
}: StoreFilterDrawerProps): ReactElement | null => {
  const drawerRef = useRef<HTMLDivElement | null>(null);

  // aria-modal 계약 이행 — 열리면 포커스를 드로어로 옮기고 Tab 을 안에 가두며,
  // 배경 스크롤을 잠그고, 닫히면 트리거("필터" 버튼)로 포커스를 되돌린다.
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const drawer = drawerRef.current;
    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    drawer?.focus();

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }

      if (event.key !== 'Tab' || drawer === null) {
        return;
      }

      // 접힌 섹션 안의 숨은 조작부는 건너뛴다 — 그러지 않으면 마지막 보이는 요소에서 포커스가 빠져나간다.
      const focusables = Array.from(
        drawer.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter((element) => element.offsetParent !== null);

      if (focusables.length === 0) {
        return;
      }

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;

      if (event.shiftKey) {
        if (active === first || !drawer.contains(active)) {
          event.preventDefault();
          last?.focus();
        }
      } else if (active === last || !drawer.contains(active)) {
        event.preventDefault();
        first?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    const previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousBodyOverflow;
      previouslyFocused?.focus();
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  // 수량·단가 입력은 blur 에 확정된다 — 닫기 전에 포커스를 떼어 입력 중이던 값을 살린다.
  const commitAndClose = (): void => {
    const active = document.activeElement;

    if (active instanceof HTMLElement) {
      active.blur();
    }

    onClose();
  };

  return (
    <div className="fixed inset-0 z-40">
      {/* 뒤를 덮는 면 — 누르면 닫힌다. 키보드 경로는 Escape 와 CLOSE 가 맡는다. */}
      <div
        aria-hidden="true"
        onClick={commitAndClose}
        className="absolute inset-0 bg-foreground/40"
      />

      <div
        ref={drawerRef}
        id={STORE_FILTER_DRAWER_ID}
        role="dialog"
        aria-modal="true"
        aria-label="필터 검색"
        tabIndex={-1}
        className="absolute inset-y-0 right-0 flex w-full max-w-md flex-col overflow-y-auto overscroll-contain border-l border-foreground/10 bg-background px-5 pt-5 pb-10 outline-none sm:px-8"
      >
        <div className="flex justify-end">
          <button
            type="button"
            onClick={commitAndClose}
            className="text-xs font-semibold tracking-[0.08em] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            CLOSE
          </button>
        </div>

        <StoreFilterPanel {...panelProps} />
      </div>
    </div>
  );
};
